import { useState, useEffect } from 'react';
import { HiEye, HiTrash, HiPhone, HiMail, HiLocationMarker } from 'react-icons/hi';
import { contactsService } from '../api/services';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import { TableSkeleton } from '../components/Skeleton';
import { sanitizeText, sanitizeErrorMessage } from '../utils/sanitize';
import { contactUpdateSchema, validateForm } from '../utils/validation';
import toast from 'react-hot-toast';

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    status: '',
    admin_notes: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await contactsService.getAll();
      setContacts(response.data);
    } catch (error) {
      toast.error(sanitizeErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleView = (contact) => {
    setSelectedContact(contact);
    setFormData({
      status: contact.status,
      admin_notes: contact.admin_notes || '',
    });
    setErrors({});
    setShowModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const validation = validateForm(contactUpdateSchema, formData);
    if (!validation.success) {
      setErrors(validation.errors);
      return;
    }

    setSubmitting(true);
    try {
      await contactsService.update(selectedContact.id, formData);
      toast.success('Contact updated successfully');
      setShowModal(false);
      fetchContacts();
    } catch (error) {
      toast.error(sanitizeErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (contact) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await contactsService.delete(contact.id);
        toast.success('Contact deleted');
        fetchContacts();
      } catch (error) {
        toast.error(sanitizeErrorMessage(error));
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns = [
    { key: 'name', label: 'Name', render: (row) => sanitizeText(row.name) },
    { key: 'phone', label: 'Phone', render: (row) => sanitizeText(row.phone) },
    { key: 'email', label: 'Email', render: (row) => sanitizeText(row.email) },
    {
      key: 'location',
      label: 'Location',
      render: (row) => {
        const city = sanitizeText(row.city);
        const state = sanitizeText(row.state);
        return city && state ? `${city}, ${state}` : city || state || '-';
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (row) => formatDate(row.created_at),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleView(row); }}
            className="p-2 text-gray-400 hover:text-gold-500 transition-colors"
            aria-label="View contact"
          >
            <HiEye className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Delete contact"
          >
            <HiTrash className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold gold-gradient-text">Contact Submissions</h1>
        <p className="text-gray-400 mt-1">Manage submissions from Contact Us form</p>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <TableSkeleton rows={5} columns={6} />
        ) : (
          <DataTable columns={columns} data={contacts} onRowClick={handleView} />
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Contact Details"
      >
        {selectedContact && (
          <div className="space-y-6">
            {/* Customer Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-dark-500 rounded-lg">
                <div className="w-12 h-12 bg-gold-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-dark-900 font-bold text-lg">
                    {sanitizeText(selectedContact.name)?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-200 truncate">
                    {sanitizeText(selectedContact.name)}
                  </p>
                  <p className="text-sm text-gray-500">{formatDate(selectedContact.created_at)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-3 text-gray-300">
                  <HiPhone className="w-5 h-5 text-gold-500 flex-shrink-0" />
                  <span className="truncate">{sanitizeText(selectedContact.phone)}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <HiMail className="w-5 h-5 text-gold-500 flex-shrink-0" />
                  <span className="truncate">{sanitizeText(selectedContact.email)}</span>
                </div>
                {(selectedContact.city || selectedContact.state) && (
                  <div className="flex items-center gap-3 text-gray-300">
                    <HiLocationMarker className="w-5 h-5 text-gold-500 flex-shrink-0" />
                    <span className="truncate">
                      {[sanitizeText(selectedContact.city), sanitizeText(selectedContact.state)]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Message</label>
              <div className="p-4 bg-dark-500 rounded-lg text-gray-200 whitespace-pre-wrap break-words">
                {sanitizeText(selectedContact.message)}
              </div>
            </div>

            {/* Update Form */}
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className={`input-field ${errors.status ? 'border-red-500' : ''}`}
                >
                  <option value="new">New</option>
                  <option value="viewed">Viewed</option>
                  <option value="responded">Responded</option>
                  <option value="closed">Closed</option>
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-red-400">{errors.status}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Admin Notes</label>
                <textarea
                  value={formData.admin_notes}
                  onChange={(e) => setFormData({ ...formData, admin_notes: e.target.value })}
                  className={`input-field ${errors.admin_notes ? 'border-red-500' : ''}`}
                  rows={3}
                  placeholder="Add internal notes..."
                  maxLength={1000}
                />
                {errors.admin_notes && (
                  <p className="mt-1 text-sm text-red-400">{errors.admin_notes}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.admin_notes.length}/1000 characters
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="btn-primary flex-1 flex items-center justify-center"
                  disabled={submitting}
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-dark-900 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Update Contact'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        )}
      </Modal>
    </div>
  );
}
